// ---------------------------------------------------------------------------
// P1 — medição do "pulo" do arrasto manual e do travamento por carga (só diagnóstico, só com "?dbg=1").
//
// NÃO altera comportamento: só lê o que o app já calcula e escreve números em buffers circulares pré-alocados.
// Sem a flag, `p1` é null e todos os ganchos retornam na primeira linha. Zero alocação por quadro.
//
// Atribuição de eventos "tardia": o que aconteceu DEPOIS do instante (±300/400 ms) só se sabe depois; cada
// ocorrência entra numa fila pequena e é classificada quando o relógio passa de t + 400 ms (ou no relatório).
// Nomes neutros nas seções ("cálculo do indicador", "janela de velas"): sem nomes de módulos internos.
// ---------------------------------------------------------------------------

import { useLayoutEffect, useRef } from 'react';
import { DBG_ENABLED } from './dbgFlag';
import { jank, EV, MODE_NAMES, NMODE, type JankRecorder } from './jankRecorder';
import { navRec } from './navRecorder';

const f = (x: number, d = 1) => (isFinite(x) ? x.toFixed(d) : '—');
const pct = (a: number, b: number) => (b > 0 ? ((a / b) * 100).toFixed(0) + '%' : '—');

// Classes de evento usadas na atribuição (máscaras do gravador)
export const M_CARGA = (1 << EV.CARGA_INICIO) | (1 << EV.CARGA_FIM) | (1 << EV.MATH_CARGA);
export const M_LOTE = (1 << EV.LOTE) | (1 << EV.LOTE_PAREDE) | (1 << EV.LOTE_DISPARO) | (1 << EV.COMMIT_LOTE) | (1 << EV.COMMIT_LOTE_PAREDE);
export const M_GC = 1 << EV.GC;
export const M_DEGRAU = 1 << EV.DEGRAU10;
export const M_DESPEJO = (1 << EV.DESPEJO) | (1 << EV.COSTURA);
/** [rótulo, máscara]; a ordem é a das linhas "tipos" dos relatórios */
export const KINDS: [string, number][] = [
  ['carga', M_CARGA], ['troca de lote', M_LOTE], ['GC provável', M_GC], ['degrau de escala', M_DEGRAU], ['despejo/costura', M_DESPEJO],
];
const NK = KINDS.length;
const kindsOf = (mask: number): string => {
  let s = '';
  for (let k = 0; k < NK; k++) if (mask & KINDS[k][1]) s += (s ? ' + ' : '') + KINDS[k][0];
  return s || 'nenhum';
};

export const K_JUMP = 0, K_MARK = 1, K_STEP = 2;
const PEND = 64;
const RESOLVE_MS = 400;          // espera antes de classificar (cobre ±300 e ±400)
const JUMP_CAP = 300, STEP_CAP = 300;
const DELTA_BINS = 1600, DELTA_W = 0.25;      // histograma de Δ (velas): bins de 0,25 até 400 velas
const JUMP_MIN_CANDLES = 5, JUMP_MEDIAN_X = 3, JUMP_WARMUP = 30;
const WIN_JUMP_MS = 300, WIN_MARK_MS = 400;
// T2: etapas de uma carga de bloco
export const S = { NET: 0, JSON: 1, NORM: 2, WIN: 3, IND: 4, DELIV: 5, REACT: 6, LOTE: 7, UPLOAD: 8, MAXF: 9 } as const;
const NS = 10;
const S_HEAD = ['rede', 'JSON', 'norm.', 'janela', 'indic.', 'entrega', 'render→commit', 'lote', 'upload', 'maior quadro'];
const S_LONG = ['rede (até os cabeçalhos)', 'leitura do JSON (corpo + análise)', 'normalização', 'janela de velas (junção, despejo, costura)', 'cálculo do indicador',
  'entrega dos dados (commitProcessed)', 'render do App até o commit do React', 'troca de lote (disparo → commit)', 'upload para a GPU (CPU dos buffers)', 'maior quadro durante a carga'];
const MARK_CAP = 200;
const FRAME_CAP = 1500;           // T3: quadros >= 25 ms guardados
const LONG_FRAME_MS = 25, IBINS = 256;
const LOAD_CAP = 64, LOAD_AFTER_COMMIT_MS = 600, LOAD_FALLBACK_MS = 5000;

export class P1Probe {
  private J: JankRecorder | null;
  constructor(J: JankRecorder | null) {
    this.J = J;
  }

  // ---------------------------------------------------------------- fila de resolução tardia
  private pK = new Uint8Array(PEND); private pI = new Int32Array(PEND); private pT = new Float64Array(PEND);
  private pN = 0;
  private enqueue(kind: number, idx: number, t: number): void {
    if (this.pN === PEND) this.resolveAt(0);
    this.pK[this.pN] = kind; this.pI[this.pN] = idx; this.pT[this.pN] = t; this.pN++;
  }
  private resolveAt(i: number): void {
    const k = this.pK[i], idx = this.pI[i];
    this.pN--;
    this.pK[i] = this.pK[this.pN]; this.pI[i] = this.pI[this.pN]; this.pT[i] = this.pT[this.pN];
    if (k === K_JUMP) this.resolveJump(idx);
    else if (k === K_STEP) this.resolveStep(idx);
    else this.resolveMark(idx);
  }
  /** Classifica o que já passou de t + 400 ms (ou tudo, com force=true, no relatório). */
  resolve(now: number, force = false): void {
    for (let i = this.pN - 1; i >= 0; i--) if (force || now - this.pT[i] >= RESOLVE_MS) this.resolveAt(i);
  }

  // ---------------------------------------------------------------- por quadro (um chamado do rAF do overlay)
  prevNow = 0; private lastMv = 0; private lastMode = 0; private dt1 = 0;
  private lastSlow = 0;
  ovN = 0; ovSum = 0; ovMax = 0;
  frames = 0;
  /** `now` = performance.now() no início do callback; `mode` = navMode; `mv` = coordenada de movimento (velas). */
  frame(now: number, mode: number, mv: number): void {
    const a = performance.now();
    const prev = this.prevNow; this.prevNow = now;
    if (prev === 0) { this.lastMv = mv; this.lastMode = mode; this.lastSlow = now; this.overhead(a); return; }
    const dt = now - prev;
    if (dt > 5000) { this.lastMv = mv; this.lastMode = mode; this.dt1 = 0; this.overhead(a); return; } // aba em segundo plano
    // P1b T6(d): 2 s depois da volta do segundo plano não entram (quadros longos, pulos)
    if (this.J && now < this.J.bgUntil) { this.lastMv = mv; this.lastMode = mode; this.dt1 = 0; this.accUF = 0; this.accRender = 0; this.accReact = 0; this.overhead(a); return; }
    this.frames++;
    // ---- T3: fecha o intervalo (tempos acumulados desde o callback anterior)
    const uf = this.accUF, rn = this.accRender, rc = this.accReact;
    this.accUF = 0; this.accRender = 0; this.accReact = 0;
    if (dt >= LONG_FRAME_MS) this.addLongFrame(now, mode, dt, uf, rn, rc);
    if (this.infoValid) {
      const o = mode * IBINS;
      this.ihC[o + this.ibin(this.iCalls)]++; this.ihT[o + this.ibin(this.iTris)]++; this.ihL[o + this.ibin(this.iLines)]++;
      this.infoFrames[mode]++;
    }
    // ---- T2: maior quadro durante a janela da carga em curso
    if (this.cur >= 0) {
      if (now > this.closeAt) this.cur = -1;
      else { const o = this.cur * NS + S.MAXF; if (dt > this.ldVal[o]) this.ldVal[o] = dt; }
    }
    // ---- T1: Δ de posição em arrasto manual (só entre dois quadros consecutivos em arrasto)
    if (mode === 1 && this.lastMode === 1) {
      const d = mv > this.lastMv ? mv - this.lastMv : this.lastMv - mv;
      let b = (d / DELTA_W) | 0; if (b >= DELTA_BINS) b = DELTA_BINS - 1;
      if (d > JUMP_MIN_CANDLES && d > JUMP_MEDIAN_X * this.dMedian && this.dragN >= JUMP_WARMUP) this.addJump(now, d, dt, this.dt1);
      this.dHist[b]++; this.dragN++;
    }
    this.lastMv = mv; this.lastMode = mode; this.dt1 = dt;
    if (now - this.lastSlow >= 250) { this.lastSlow = now; this.slow(now); }
    this.overhead(a);
  }
  private overhead(a: number) { const ms = performance.now() - a; this.ovN++; this.ovSum += ms; if (ms > this.ovMax) this.ovMax = ms; }
  /** a cada ~250 ms: mediana de Δ e classificação tardia */
  private slow(now: number): void {
    // mediana de Δ do arrasto (histograma)
    const n = this.dragN;
    if (n >= 10) {
      let acc = 0; const half = n / 2;
      for (let i = 0; i < DELTA_BINS; i++) { acc += this.dHist[i]; if (acc >= half) { this.dMedian = (i + 0.5) * DELTA_W; break; } }
    }
    this.resolve(now);
  }

  // ---------------------------------------------------------------- T1: pulos no arrasto manual
  dragN = 0; dMedian = 0;
  private dHist = new Uint32Array(DELTA_BINS);
  jumpN = 0; jumpMax = 0;
  private jHead = 0;
  jT = new Float64Array(JUMP_CAP); jD = new Float32Array(JUMP_CAP); jDt = new Float32Array(JUMP_CAP); jDtPrev = new Float32Array(JUMP_CAP);
  jMask = new Uint32Array(JUMP_CAP); jMed = new Float32Array(JUMP_CAP);
  readonly jKind = new Float64Array(NK); jNone = 0; jResolved = 0;
  // P1b T5: classe do pulo (0 dedo: aplicado ≈ pedido ±10%; 1 alcance: aplicado > pedido + 5 velas; 2 segura: o resto)
  // e repintura do indicador em ±300 ms
  jCls = new Uint8Array(JUMP_CAP); jPed = new Float32Array(JUMP_CAP); readonly jClsN = new Float64Array(3);
  jRep = new Uint8Array(JUMP_CAP); jRepN = 0;
  /** de onde vem o Δ pedido pelo dedo no último quadro (o gravador de voo; os testes trocam) */
  dragSrc: { lastPed: number } | null = navRec;
  private addJump(now: number, d: number, dt: number, dtPrev: number): void {
    const i = this.jHead; this.jHead = (i + 1) % JUMP_CAP;
    this.jT[i] = now; this.jD[i] = d; this.jDt[i] = dt; this.jDtPrev[i] = dtPrev; this.jMask[i] = 0; this.jMed[i] = this.dMedian;
    this.jumpN++; if (d > this.jumpMax) this.jumpMax = d;
    const ped = this.dragSrc ? Math.abs(this.dragSrc.lastPed) : NaN;
    const cls = !(ped === ped) ? 2 : Math.abs(d - ped) <= 0.1 * ped ? 0 : d > ped + 5 ? 1 : 2;
    this.jCls[i] = cls; this.jPed[i] = ped; this.jClsN[cls]++; this.jRep[i] = 0;
    this.enqueue(K_JUMP, i, now);
  }
  private resolveJump(i: number): void {
    const t = this.jT[i];
    const m = this.J ? this.J.maskBetween(t - WIN_JUMP_MS, t + WIN_JUMP_MS) : 0;
    this.jMask[i] = m; this.jResolved++;
    if (this.repaint) { this.repaint.repaintNear(t, WIN_JUMP_MS); if (this.repaint.nearM === this.repaint.nearM) { this.jRep[i] = 1; this.jRepN++; } }
    let any = false;
    for (let k = 0; k < NK; k++) if (m & KINDS[k][1]) { this.jKind[k]++; any = true; }
    if (!any) this.jNone++;
  }

  // ---------------------------------------------------------------- T5: degraus de escala >= 10% por quadro
  stepN = 0; private stHead = 0;
  stepSwitchN = 0; private switchActive = false; private switchEndT = -1e9;
  /** Troca de série começou (true) ou a série nova foi entregue (false). */
  seriesSwitch(now: number, active: boolean): void { this.switchActive = active; if (!active) this.switchEndT = now; }
  readonly stMode = new Uint8Array(STEP_CAP); stT = new Float64Array(STEP_CAP); stPct = new Float32Array(STEP_CAP);
  stDt = new Float32Array(STEP_CAP); stMask = new Uint32Array(STEP_CAP);
  readonly stCount = new Float64Array(NMODE);
  /** Chamado por dbgScaleFrame quando a faixa desenhada variou >= 10% entre dois quadros. */
  scaleStep(now: number, stepPct: number, dtMs: number): void {
    // P1b T6(c): degraus durante a troca de série (e até 1,5 s depois) são a troca, não degrau de escala
    if (this.switchActive || now - this.switchEndT < 1500) { this.stepSwitchN++; return; }
    const i = this.stHead; this.stHead = (i + 1) % STEP_CAP;
    const m = this.J ? this.J.lastMode : 0;
    this.stT[i] = now; this.stPct[i] = stepPct; this.stMode[i] = m; this.stDt[i] = dtMs; this.stMask[i] = 0;
    this.stepN++; this.stCount[m]++;
    this.enqueue(K_STEP, i, now);
  }
  private resolveStep(i: number): void {
    const t = this.stT[i];
    this.stMask[i] = this.J ? this.J.maskBetween(t - WIN_JUMP_MS, t + WIN_JUMP_MS) & ~M_DEGRAU : 0;
  }

  // ---------------------------------------------------------------- T3: decomposição do quadro
  private accUF = 0; private accRender = 0; private accReact = 0;
  /** Tempo de um handler de useFrame (somado no quadro). */
  addUF(ms: number): void { this.accUF += ms; }
  /** Tempo de uma chamada de renderer.render (CPU da submissão). */
  addRender(ms: number): void { this.accRender += ms; }
  /** Tempo do React do início do render até o commit (useP1RenderCommit; sem o Profiler, que não roda em produção). */
  addReact(ms: number): void { this.accReact += ms; }
  lfN = 0; private lfHead = 0;
  lfT = new Float64Array(FRAME_CAP); lfMode = new Uint8Array(FRAME_CAP);
  lfDur = new Float32Array(FRAME_CAP); lfUF = new Float32Array(FRAME_CAP); lfRender = new Float32Array(FRAME_CAP); lfReact = new Float32Array(FRAME_CAP);
  readonly lfCount = new Float64Array(NMODE);
  private addLongFrame(now: number, mode: number, dt: number, uf: number, rn: number, rc: number): void {
    const i = this.lfHead; this.lfHead = (i + 1) % FRAME_CAP; this.lfN++; this.lfCount[mode]++;
    this.lfT[i] = now; this.lfMode[i] = mode; this.lfDur[i] = dt; this.lfUF[i] = uf; this.lfRender[i] = rn; this.lfReact[i] = rc;
  }
  // renderer.info (calls, triângulos, linhas) por modo: histograma em escala logarítmica (8 bins por oitava)
  private iCalls = 0; private iTris = 0; private iLines = 0; private infoValid = false;
  private ihC = new Uint32Array(NMODE * IBINS); private ihT = new Uint32Array(NMODE * IBINS); private ihL = new Uint32Array(NMODE * IBINS);
  readonly infoFrames = new Float64Array(NMODE);
  private ibin(v: number): number { const b = (Math.log2(v + 1) * 8) | 0; return b >= IBINS ? IBINS - 1 : b < 0 ? 0 : b; }
  /** Valores de renderer.info.render do último desenho (lidos no início do quadro seguinte). */
  info(calls: number, tris: number, lines: number): void { this.iCalls = calls; this.iTris = tris; this.iLines = lines; this.infoValid = true; }
  private infoMedian(h: Uint32Array, mode: number): number {
    const o = mode * IBINS; let total = 0;
    for (let i = 0; i < IBINS; i++) total += h[o + i];
    if (total === 0) return NaN;
    let acc = 0;
    for (let i = 0; i < IBINS; i++) { acc += h[o + i]; if (acc >= total / 2) return i === 0 ? 0 : Math.pow(2, (i + 0.5) / 8) - 1; }
    return NaN;
  }
  /** QUADRO EM ARRASTO (T3): para quadros >= 25 ms, por modo, mediana/p90 de cada parte; e renderer.info (mediana). */
  framesReport(): string[] {
    const L: string[] = [];
    const sz = Math.min(this.lfN, FRAME_CAP);
    const q = (a: number[], p: number) => { if (!a.length) return NaN; a.sort((x, y) => x - y); return a[Math.min(a.length - 1, Math.floor(p * a.length))]; };
    const mp = (a: number[]) => `${f(q(a, 0.5), 1)}/${f(q(a, 0.9), 0)}`;
    L.push(`=== QUADRO EM ARRASTO: quadros >=25ms por modo, mediana/p90 em ms (${this.lfN} quadros; "não atribuído" = o resto do intervalo) ===`);
    L.push('modo | quadros | quadro | useFrame | submissão CPU | React (render→commit) | não atribuído');
    if (this.J) L.push(`voltas do segundo plano: ${this.J.bgN} (maior congelamento ${f(this.J.bgMaxFreeze, 0)} ms; 2 s depois de cada volta fora das estatísticas)`);
    const order = [1, 0, 2, 3, 4];
    for (const m of order) {
      const dur: number[] = [], uf: number[] = [], rn: number[] = [], rc: number[] = [], un: number[] = [];
      for (let k = 0; k < sz; k++) {
        const i = (this.lfHead - sz + k + FRAME_CAP * 2) % FRAME_CAP;
        if (this.lfMode[i] !== m) continue;
        dur.push(this.lfDur[i]); uf.push(this.lfUF[i]); rn.push(this.lfRender[i]); rc.push(this.lfReact[i]);
        const r = this.lfDur[i] - this.lfUF[i] - this.lfRender[i] - this.lfReact[i]; un.push(r > 0 ? r : 0);
      }
      if (!dur.length) continue;
      L.push(`${MODE_NAMES[m]} | ${dur.length} | ${mp(dur)} | ${mp(uf)} | ${mp(rn)} | ${mp(rc)} | ${mp(un)}`);
    }
    const k = (x: number) => (x !== x ? '—' : x >= 10000 ? (x / 1000).toFixed(0) + 'k' : x >= 1000 ? (x / 1000).toFixed(1) + 'k' : x.toFixed(0));
    const inf: string[] = [];
    for (const m of order) if (this.infoFrames[m] > 0) inf.push(`${MODE_NAMES[m]}: ${k(this.infoMedian(this.ihC, m))} chamadas / ${k(this.infoMedian(this.ihT, m))} triângulos / ${k(this.infoMedian(this.ihL, m))} linhas`);
    L.push(`desenho por quadro (mediana de todos os quadros): ${inf.join(' | ') || '—'}`);
    return L;
  }

  // ---------------------------------------------------------------- T2: travamento por carga, por etapa
  loadN = 0; private ldHead = 0;
  ldT = new Float64Array(LOAD_CAP); ldKind = new Uint8Array(LOAD_CAP); ldVal = new Float32Array(LOAD_CAP * NS).fill(NaN);
  private cur = -1; private closeAt = 0; private lastDisparo = 0;
  loadStart(now: number): void {
    const i = this.ldHead; this.ldHead = (i + 1) % LOAD_CAP; this.loadN++;
    this.ldT[i] = now; this.ldKind[i] = 0;
    const o = i * NS; for (let k = 0; k < NS; k++) this.ldVal[o + k] = NaN;
    this.ldVal[o + S.MAXF] = 0;
    this.cur = i; this.closeAt = now + 30_000;
  }
  /** Soma `ms` na etapa da carga em curso (ou fica com o maior valor, se `max`). Sem carga em curso: nada. */
  loadStage(s: number, ms: number, max = false): void {
    const i = this.cur; if (i < 0) return;
    const o = i * NS + s, v = this.ldVal[o];
    this.ldVal[o] = v !== v ? ms : max ? (ms > v ? ms : v) : v + ms;
  }
  loadNewer(): void { if (this.cur >= 0) this.ldKind[this.cur] = 1; }
  /** A carga terminou sem entrega de dados (fim do histórico, falha): a janela de observação fecha logo depois. */
  loadNoData(now: number): void { if (this.cur >= 0) this.closeAt = now + LOAD_AFTER_COMMIT_MS; }
  /** A carga entregou dados: o commit do React ainda vem (ou não, se não mudou nada): fecha em até 5 s. */
  loadDelivered(now: number): void { if (this.cur >= 0 && this.closeAt > now + LOAD_FALLBACK_MS) this.closeAt = now + LOAD_FALLBACK_MS; }
  /** O commit do React dos dados da carga foi visto (efeito pós-commit): `ms` desde a entrega. */
  loadCommit(now: number, ms: number): void {
    if (this.cur < 0) return;
    this.loadStage(S.REACT, ms);
    this.closeAt = now + LOAD_AFTER_COMMIT_MS;
  }
  loteDisparo(now: number): void { this.lastDisparo = now; }
  loteCommit(now: number): void { if (this.cur >= 0 && this.lastDisparo > 0 && now >= this.lastDisparo) this.loadStage(S.LOTE, now - this.lastDisparo, true); }
  /** Quantidade de cargas com a etapa medida / média / p90 (relatório). */
  private stageStats(s: number, out: Float64Array): void {
    const sz = Math.min(this.loadN, LOAD_CAP); let n = 0, sum = 0;
    const v: number[] = [];
    for (let k = 0; k < sz; k++) { const x = this.ldVal[k * NS + s]; if (x === x) { n++; sum += x; v.push(x); } }
    v.sort((a, b) => a - b);
    out[0] = n; out[1] = n ? sum / n : NaN; out[2] = n ? v[Math.min(n - 1, Math.floor(0.9 * n))] : NaN;
  }
  /** TRAVAMENTO POR CARGA (T2): últimas `rows` cargas e média/p90 por etapa. */
  loadsReport(t0Abs: number, rows: number): string[] {
    const L: string[] = [];
    const sz = Math.min(this.loadN, LOAD_CAP);
    L.push(`=== TRAVAMENTO POR CARGA: ${this.loadN} cargas (ms por etapa; — = não ocorreu/não medido) ===`);
    L.push(`# | t | ${S_HEAD.join(' | ')}`);
    const c = (x: number) => (x === x ? (x >= 100 ? x.toFixed(0) : x.toFixed(1)) : '—');
    for (let k = Math.max(0, sz - rows); k < sz; k++) {
      const i = (this.ldHead - sz + k + LOAD_CAP * 2) % LOAD_CAP, o = i * NS;
      const vals: string[] = []; for (let q = 0; q < NS; q++) vals.push(c(this.ldVal[o + q]));
      L.push(`${this.loadN - sz + k + 1}${this.ldKind[i] ? 'N' : ''} | ${f((this.ldT[i] - t0Abs) / 1000, 0)}s | ${vals.join(' | ')}`);
    }
    const st = new Float64Array(3);
    const avg: string[] = [], p90: string[] = [];
    for (let q = 0; q < NS; q++) { this.stageStats(q, st); avg.push(c(st[1])); p90.push(c(st[2])); }
    L.push(`média | | ${avg.join(' | ')}`);
    L.push(`p90 | | ${p90.join(' | ')}`);
    return L;
  }

  // ---------------------------------------------------------------- T4: marcas de filamento
  /** Fonte das repinturas (a sonda de sincronia se registra aqui; evita import circular). */
  repaint: { repaintNear(t: number, halfMs: number): void; nearM: number; nearY: number } | null = null;
  markN = 0; private mkHead = 0;
  mkT = new Float64Array(MARK_CAP); mkMode = new Uint8Array(MARK_CAP); mkPos = new Float32Array(MARK_CAP);
  mkMask = new Uint32Array(MARK_CAP); mkLong = new Float32Array(MARK_CAP); mkRepM = new Float32Array(MARK_CAP); mkRepY = new Float32Array(MARK_CAP);
  /** O Mateus tocou em "Marcar filamento". */
  mark(now: number, mode: number, pos: number): void {
    const i = this.mkHead; this.mkHead = (i + 1) % MARK_CAP; this.markN++;
    this.mkT[i] = now; this.mkMode[i] = mode; this.mkPos[i] = pos; this.mkMask[i] = 0; this.mkLong[i] = 0; this.mkRepM[i] = NaN; this.mkRepY[i] = NaN;
    this.enqueue(K_MARK, i, now);
  }
  private resolveMark(i: number): void {
    const t = this.mkT[i];
    if (this.J) { this.mkMask[i] = this.J.maskBetween(t - WIN_MARK_MS, t + WIN_MARK_MS); this.mkLong[i] = this.J.longestBetween(t - WIN_MARK_MS, t + WIN_MARK_MS); }
    if (this.repaint) { this.repaint.repaintNear(t, WIN_MARK_MS); this.mkRepM[i] = this.repaint.nearM; this.mkRepY[i] = this.repaint.nearY; }
  }
  /** MARCAS DE FILAMENTO (T4): o que aconteceu em ±400 ms de cada toque. */
  marksReport(t0Abs: number, lines: number): string[] {
    this.resolve(performance.now(), true);
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sz = Math.min(this.markN, MARK_CAP);
    let rep = 0, car = 0, des = 0, lng = 0, deg = 0;
    for (let k = 0; k < sz; k++) {
      const i = (this.mkHead - sz + k + MARK_CAP * 2) % MARK_CAP, m = this.mkMask[i];
      if (this.mkRepM[i] === this.mkRepM[i]) rep++;
      if (m & M_CARGA) car++;
      if (m & M_DESPEJO) des++;
      if (this.mkLong[i] > 0) lng++;
      if (m & M_DEGRAU) deg++;
    }
    L.push(`=== MARCAS DE FILAMENTO: ${this.markN} | em ±${WIN_MARK_MS}ms: repintura ${rep} (${pct(rep, sz)}) | carga ${car} | despejo/costura ${des} | quadro longo ${lng} | degrau de escala ${deg} ===`);
    for (let k = Math.max(0, sz - lines); k < sz; k++) {
      const i = (this.mkHead - sz + k + MARK_CAP * 2) % MARK_CAP, m = this.mkMask[i];
      const parts: string[] = [];
      if (this.mkRepM[i] === this.mkRepM[i]) parts.push(`repintura (momentum ${f(this.mkRepM[i], 1)}% | Y ${f(this.mkRepY[i], 2)}%)`);
      if (m & M_CARGA) parts.push('carga');
      if (m & M_DESPEJO) parts.push('despejo/costura');
      if (this.mkLong[i] > 0) parts.push(`quadro ${f(this.mkLong[i], 0)}ms`);
      if (m & M_DEGRAU) parts.push('degrau de escala');
      L.push(`t=${rel(this.mkT[i])} ${MODE_NAMES[this.mkMode[i]]} pos=${f(this.mkPos[i], 0)} → ${parts.join(' | ') || 'nada em ±400ms'}`);
    }
    return L;
  }

  // ---------------------------------------------------------------- relatórios
  /** PULOS NO ARRASTO (T1). `lines` = quantos pulos listar. */
  jumpsReport(t0Abs: number, lines: number): string[] {
    this.resolve(performance.now(), true);
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sz = Math.min(this.jumpN, JUMP_CAP);
    L.push(`=== PULOS NO ARRASTO: ${this.jumpN} | maior ${f(this.jumpMax, 0)} velas | Δ mediano ${f(this.dMedian, 1)} velas/quadro (limiar ${f(Math.max(JUMP_MIN_CANDLES, JUMP_MEDIAN_X * this.dMedian), 1)}) | quadros em arrasto ${this.dragN} ===`);
    const rows: string[] = [];
    for (let k = 0; k < NK; k++) rows.push(`${KINDS[k][0]} ${pct(this.jKind[k], this.jResolved)}`);
    rows.push(`nenhum ${pct(this.jNone, this.jResolved)}`);
    L.push(`tipo do evento em ±${WIN_JUMP_MS}ms (% dos pulos; um pulo pode ter vários): ${rows.join(' | ')}`);
    // P1b T5: classe do pulo (dedo × vista) e cada tipo de evento contra a taxa ao acaso (janelas aleatórias de ±300 ms)
    L.push(`classe: dedo ${this.jClsN[0]} | alcance ${this.jClsN[1]} | segura ${this.jClsN[2]} (dedo: vista ≈ pedido ±10%; alcance: vista > pedido + 5 velas)`);
    const ch = this.chanceRates(WIN_JUMP_MS);
    if (ch) {
      const r2: string[] = [];
      for (let k = 0; k < NK; k++) r2.push(`${KINDS[k][0]} ${pct(this.jKind[k], this.jResolved)} (acaso ${f(ch[k] * 100, 0)}%)`);
      r2.push(`repintura ${pct(this.jRepN, this.jResolved)} (acaso ${f(ch[NK] * 100, 0)}%)`);
      L.push(`contra o acaso: ${r2.join(' | ')}`);
    }
    for (let k = Math.max(0, sz - lines); k < sz; k++) {
      const i = (this.jHead - sz + k + JUMP_CAP * 2) % JUMP_CAP;
      L.push(`t=${rel(this.jT[i])} Δ=${f(this.jD[i], 0)} velas (dedo ${f(this.jPed[i], 0)}, ${['dedo', 'alcance', 'segura'][this.jCls[i]]}) | quadros: ${f(this.jDt[i], 0)}ms (o que precedeu o pulo) e ${f(this.jDtPrev[i], 0)}ms (o anterior) [${kindsOf(this.jMask[i])}]`);
    }
    return L;
  }

  /**
   * P1b T5: taxa ao acaso de cada tipo de evento: fração de 400 janelas aleatórias de ±halfMs (semente fixa), dentro
   * do trecho que o anel de eventos ainda cobre, com o evento presente. Último elemento = repintura. null sem dados.
   */
  chanceRates(halfMs: number): Float64Array | null {
    if (!this.J) return null;
    const now = this.prevNow || performance.now(), s0 = Math.max(this.J.oldestEventT(), this.J.t0) + halfMs, s1 = now - halfMs;   // relógio dos quadros
    if (!(s1 > s0)) return null;
    const out = new Float64Array(NK + 1), N = 400;
    let seed = 12345;
    for (let q = 0; q < N; q++) {
      seed = (seed * 1103515245 + 12345) % 2147483648;
      const t = s0 + (seed / 2147483648) * (s1 - s0), m = this.J.maskBetween(t - halfMs, t + halfMs);
      for (let k = 0; k < NK; k++) if (m & KINDS[k][1]) out[k]++;
      if (this.repaint) { this.repaint.repaintNear(t, halfMs); if (this.repaint.nearM === this.repaint.nearM) out[NK]++; }
    }
    for (let k = 0; k <= NK; k++) out[k] /= N;
    return out;
  }

  /** DEGRAUS DE ESCALA (T5). */
  stepsReport(t0Abs: number, lines: number): string[] {
    this.resolve(performance.now(), true);
    const L: string[] = [];
    const rel = (t: number) => f((t - t0Abs) / 1000, 1) + 's';
    const sz = Math.min(this.stepN, STEP_CAP);
    const per: string[] = [];
    for (let m = 0; m < NMODE; m++) if (this.stCount[m] > 0) per.push(`${MODE_NAMES[m]} ${this.stCount[m]}`);
    L.push(`=== DEGRAUS DE ESCALA >=10% por quadro: ${this.stepN} | ${per.join(' | ') || 'nenhum'} | fora: ${this.stepSwitchN} na troca de série ===`);
    for (let k = Math.max(0, sz - lines); k < sz; k++) {
      const i = (this.stHead - sz + k + STEP_CAP * 2) % STEP_CAP;
      L.push(`t=${rel(this.stT[i])} ${f(this.stPct[i], 0)}% ${MODE_NAMES[this.stMode[i]]} quadro anterior ${f(this.stDt[i], 0)}ms [${kindsOf(this.stMask[i])}]`);
    }
    return L;
  }
}

// ---------------------------------------------------------------------------
// Singleton + ganchos. Sem a flag: nada é criado e os ganchos retornam na primeira linha.
// ---------------------------------------------------------------------------
export const p1: P1Probe | null = DBG_ENABLED ? new P1Probe(jank) : null;

/** dbgScaleFrame: degrau >= 10% da faixa entre dois quadros. */
export function p1ScaleStep(stepPct: number, dtMs: number): void {
  if (!DBG_ENABLED) return;
  p1!.scaleStep(performance.now(), stepPct, dtMs);
}

// ---- T2: ganchos da carga de bloco (no-op sem a flag)
export function p1LoadStart(): void { if (DBG_ENABLED) p1!.loadStart(performance.now()); }
export function p1LoadStage(s: number, ms: number, max = false): void { if (DBG_ENABLED) p1!.loadStage(s, ms, max); }
export function p1LoadNewer(): void { if (DBG_ENABLED) p1!.loadNewer(); }
export function p1LoadDone(delivered: boolean): void { if (DBG_ENABLED) (delivered ? p1!.loadDelivered(performance.now()) : p1!.loadNoData(performance.now())); }
export function p1LoadCommit(ms: number): void { if (DBG_ENABLED) p1!.loadCommit(performance.now(), ms); }
export function p1LoteDisparo(): void { if (DBG_ENABLED) p1!.loteDisparo(performance.now()); }
export function p1LoteCommit(): void { if (DBG_ENABLED) p1!.loteCommit(performance.now()); }
/** Início de uma etapa: devolve o relógio (0 sem a flag); `p1End(s, t0)` fecha e soma na carga em curso. */
export function p1T(): number { return DBG_ENABLED ? performance.now() : 0; }
export function p1End(s: number, t0: number, max = false): void { if (DBG_ENABLED) p1!.loadStage(s, performance.now() - t0, max); }
/** Blocos do profiler existente: os uploads de buffers (CPU) entram na etapa "upload" da carga em curso. */
export function p1Block(name: string, ms: number): void {
  if (!DBG_ENABLED) return;
  if (name.charCodeAt(0) === 87 /* 'W' */ && name.startsWith('WebGL:')) p1!.loadStage(S.UPLOAD, ms);
  // P1b T6(b): o tempo do React vem de useP1RenderCommit (o Profiler não roda em produção e daria 0)
}

/** P1b T6(c): troca de série começou (true) / série nova entregue (false). */
export function p1SeriesSwitch(active: boolean): void { if (DBG_ENABLED) p1!.seriesSwitch(performance.now(), active); }

/**
 * P1b T6(b): tempo do React do início do render deste componente até o commit (useLayoutEffect), sem o Profiler.
 * Chamar no topo do componente. Sem a flag não usa hook nenhum (DBG_ENABLED é constante do módulo: a ordem dos
 * hooks não muda).
 */
export function useP1RenderCommit(): void {
  if (!DBG_ENABLED) return;
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const t = useRef(0);
  t.current = performance.now();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useLayoutEffect(() => { p1!.addReact(performance.now() - t.current); });
}
